<template>
  <Teleport to="body">
    <Transition name="modal-fade">
      <div
        v-if="visible"
        class="ktm-overlay"
        data-component="Kanban/KanbanTransitionModal"
        @click.self="$emit('close')"
      >
        <div class="ktm-modal" role="dialog" :aria-labelledby="`ktm-title-${visit.id}`">

          <!-- ── Modal header ───────────────────────────────────────── -->
          <header class="ktm-modal__header" :style="{ '--col-color': currentColumn.color }">
            <div class="ktm-modal__header-left">
              <i :class="`${currentColumn.icon} ktm-modal__col-icon`"></i>
              <div>
                <p class="ktm-modal__col-label">{{ currentColumn.label }}</p>
                <h3 class="ktm-modal__title" :id="`ktm-title-${visit.id}`">{{ modalConfig.title }}</h3>
              </div>
            </div>
            <button class="ktm-modal__close" @click="$emit('close')" aria-label="Fermer">
              <i class="fas fa-times"></i>
            </button>
          </header>

          <!-- ── Visit summary strip ───────────────────────────────── -->
          <div class="ktm-modal__summary">
            <span class="ktm-modal__nsil">{{ visit.nsil }}</span>
            <span class="ktm-modal__address">{{ visit.address.zipCode }} {{ visit.address.city }} — {{ visit.address.street }}</span>
            <span v-if="visit.mandant" class="ktm-modal__mandant">{{ visit.mandant }}</span>
          </div>

          <!-- ── Body: contextual form ────────────────────────────── -->
          <div class="ktm-modal__body">

            <!-- État: À programmer → saisir une date de planification -->
            <template v-if="visit.columnId === 'a-programmer'">
              <div class="ktm-modal__field">
                <label class="ktm-modal__label" for="ktm-planified-at">
                  <i class="fas fa-calendar-check"></i>
                  Date de visite planifiée <span class="ktm-modal__required">*</span>
                </label>
                <input
                  id="ktm-planified-at"
                  v-model="form.planifiedAt"
                  type="date"
                  class="ktm-modal__input"
                  :min="today"
                />
              </div>
              <p class="ktm-modal__hint">
                <i class="fas fa-info-circle"></i>
                La planification déplacera la carte vers <strong>À attribuer</strong>.
              </p>
            </template>

            <!-- État: À attribuer → choisir un technicien -->
            <template v-else-if="visit.columnId === 'a-attribuer'">
              <div class="ktm-modal__field">
                <label class="ktm-modal__label" for="ktm-manager">
                  <i class="fas fa-user-plus"></i>
                  Technicien responsable <span class="ktm-modal__required">*</span>
                </label>
                <select id="ktm-manager" v-model="form.managerId" class="ktm-modal__select">
                  <option value="">— Sélectionner un technicien —</option>
                  <option v-for="user in users" :key="user.id" :value="user.id">
                    {{ user.fullName || `${user.firstName || ''} ${user.lastName || ''}`.trim() }}
                    <template v-if="user.team"> ({{ user.team.name }})</template>
                  </option>
                </select>
              </div>
              <div class="ktm-modal__field" v-if="visit.scheduledDate">
                <label class="ktm-modal__label"><i class="fas fa-calendar-alt"></i> Date planifiée</label>
                <p class="ktm-modal__value">{{ formatDate(visit.scheduledDate) }}</p>
              </div>
              <p class="ktm-modal__hint">
                <i class="fas fa-info-circle"></i>
                L'attribution déplacera la carte vers <strong>En cours</strong>.
              </p>
            </template>

            <!-- État: En cours → saisir la visite -->
            <template v-else-if="visit.columnId === 'en-cours'">
              <div class="ktm-modal__field">
                <label class="ktm-modal__label" for="ktm-visit-date">
                  <i class="fas fa-calendar-day"></i>
                  Date de la visite <span class="ktm-modal__required">*</span>
                </label>
                <input
                  id="ktm-visit-date"
                  v-model="form.visitDate"
                  type="date"
                  class="ktm-modal__input"
                />
              </div>
              <div class="ktm-modal__field">
                <label class="ktm-modal__label" for="ktm-visit-type">
                  <i class="fas fa-tag"></i>
                  Type de visite
                </label>
                <select id="ktm-visit-type" v-model="form.visitType" class="ktm-modal__select">
                  <option value="">— Sélectionner —</option>
                  <option value="visite_préalable">Visite préalable</option>
                  <option value="visite_perinatale">Visite périnatale</option>
                  <option value="visite_annuelle">Visite annuelle</option>
                  <option value="visite_sortante">Visite sortante</option>
                </select>
              </div>
              <div class="ktm-modal__field">
                <label class="ktm-modal__label" for="ktm-visit-comment">
                  <i class="fas fa-comment-alt"></i>
                  Commentaire
                </label>
                <textarea
                  id="ktm-visit-comment"
                  v-model="form.comment"
                  class="ktm-modal__textarea"
                  rows="3"
                  placeholder="Observations, remarques..."
                ></textarea>
              </div>
              <p class="ktm-modal__hint">
                <i class="fas fa-info-circle"></i>
                La saisie déplacera la carte vers <strong>Pré-chiffrage</strong>.
              </p>
            </template>

            <!-- État: Pré-chiffrage → accéder à la fiche -->
            <template v-else-if="visit.columnId === 'pre-chiffrage'">
              <div class="ktm-modal__info-block">
                <i class="fas fa-calculator ktm-modal__info-icon"></i>
                <div>
                  <p class="ktm-modal__info-text">Le pré-chiffrage est à compléter directement dans la fiche du dossier.</p>
                  <p class="ktm-modal__info-sub">
                    Visite réalisée le
                    <strong>{{ visit.lastVisitDate ? formatDate(visit.lastVisitDate) : 'date inconnue' }}</strong>
                    <template v-if="visit.technicalManager"> par <strong>{{ visit.technicalManager }}</strong></template>.
                  </p>
                </div>
              </div>
              <p class="ktm-modal__hint">
                <i class="fas fa-info-circle"></i>
                Une fois le pré-chiffrage terminé la carte passera en <strong>Terminé</strong>.
              </p>
            </template>

            <!-- État: Terminé → résumé lecture seule -->
            <template v-else-if="visit.columnId === 'termine'">
              <div class="ktm-modal__done">
                <i class="fas fa-check-circle ktm-modal__done-icon"></i>
                <p>Cette visite est <strong>terminée</strong>.</p>
              </div>
              <div class="ktm-modal__summary-rows">
                <div v-if="visit.technicalManager" class="ktm-modal__summary-row">
                  <i class="fas fa-user"></i>
                  <span>{{ visit.technicalManager }}</span>
                </div>
                <div v-if="visit.lastVisitDate" class="ktm-modal__summary-row">
                  <i class="fas fa-calendar-check"></i>
                  <span>Visite le {{ formatDate(visit.lastVisitDate) }}</span>
                </div>
                <div v-if="visit.scheduledDate" class="ktm-modal__summary-row">
                  <i class="fas fa-calendar-alt"></i>
                  <span>Planifiée le {{ formatDate(visit.scheduledDate) }}</span>
                </div>
              </div>
            </template>

          </div><!-- /.ktm-modal__body -->

          <!-- ── Footer actions ────────────────────────────────────── -->
          <footer class="ktm-modal__footer">
            <button class="ktm-modal__btn ktm-modal__btn--cancel" @click="$emit('close')">
              Annuler
            </button>

            <!-- À programmer / À attribuer / En cours → CTA de transition -->
            <button
              v-if="['a-programmer', 'a-attribuer', 'en-cours'].includes(visit.columnId)"
              class="ktm-modal__btn ktm-modal__btn--confirm"
              :style="{ '--btn-color': currentColumn.color }"
              :disabled="!canConfirm"
              @click="handleConfirm"
            >
              <i :class="modalConfig.ctaIcon"></i>
              {{ modalConfig.cta }}
            </button>

            <!-- Pré-chiffrage / Terminé → lien vers le dossier -->
            <router-link
              v-else
              :to="`/dossier/${visit.id}`"
              class="ktm-modal__btn ktm-modal__btn--link"
              :style="{ '--btn-color': currentColumn.color }"
              @click="$emit('close')"
            >
              <i class="fas fa-folder-open"></i>
              {{ modalConfig.cta }}
            </router-link>
          </footer>

        </div>
      </div>
    </Transition>
  </Teleport>
</template>

<script>
// Column definitions (colors) — mirrors KanbanVisites.vue constants
const COLUMN_META = {
  'a-programmer':  { label: 'À programmer',  color: '#F39178', icon: 'fas fa-clock' },
  'a-attribuer':   { label: 'À attribuer',   color: '#6E88FF', icon: 'fas fa-user-plus' },
  'en-cours':      { label: 'En cours',       color: '#00CFB1', icon: 'fas fa-bolt' },
  'pre-chiffrage': { label: 'Pré-chiffrage',  color: '#68CFDF', icon: 'fas fa-calculator' },
  'termine':       { label: 'Terminé',        color: '#61BC80', icon: 'fas fa-check-circle' }
}

const MODAL_CONFIG = {
  'a-programmer':  { title: 'Programmer la visite',  cta: 'Planifier',           ctaIcon: 'fas fa-calendar-check' },
  'a-attribuer':   { title: 'Attribuer la visite',   cta: 'Attribuer',           ctaIcon: 'fas fa-user-check' },
  'en-cours':      { title: 'Saisir la visite',      cta: 'Valider la visite',   ctaIcon: 'fas fa-check' },
  'pre-chiffrage': { title: 'Pré-chiffrage en cours', cta: 'Ouvrir le dossier',  ctaIcon: 'fas fa-folder-open' },
  'termine':       { title: 'Visite terminée',        cta: 'Voir le dossier',    ctaIcon: 'fas fa-folder-open' }
}

export default {
  name: 'KanbanTransitionModal',
  props: {
    visible: {
      type: Boolean,
      default: false
    },
    visit: {
      type: Object,
      required: true
      // visit shape: { id, nsil, columnId, address, mandant, technicalManager,
      //                scheduledDate, lastVisitDate, occupancy, propertyType }
    },
    users: {
      type: Array,
      default: () => []
      // users shape: [{ id, fullName, firstName, lastName, team }]
    }
  },
  emits: ['close', 'transition-confirmed'],
  data() {
    return {
      form: {
        planifiedAt: '',
        managerId: '',
        visitDate: '',
        visitType: '',
        comment: ''
      }
    }
  },
  computed: {
    currentColumn() {
      return COLUMN_META[this.visit?.columnId] || COLUMN_META['a-programmer']
    },
    modalConfig() {
      return MODAL_CONFIG[this.visit?.columnId] || MODAL_CONFIG['a-programmer']
    },
    today() {
      return new Date().toISOString().split('T')[0]
    },
    canConfirm() {
      switch (this.visit?.columnId) {
        case 'a-programmer': return !!this.form.planifiedAt
        case 'a-attribuer':  return !!this.form.managerId
        case 'en-cours':     return !!this.form.visitDate
        default:             return true
      }
    }
  },
  watch: {
    visible(val) {
      if (val) {
        // Reset form on open
        this.form = { planifiedAt: '', managerId: '', visitDate: '', visitType: '', comment: '' }
      }
    }
  },
  methods: {
    formatDate(dateStr) {
      if (!dateStr) return ''
      return new Date(dateStr).toLocaleDateString('fr-FR', { day: '2-digit', month: '2-digit', year: 'numeric' })
    },
    handleConfirm() {
      if (!this.canConfirm) return
      // ── Présentation uniquement ──────────────────────────────────────────
      // Pour brancher le backend, remplacer ce $emit par l'appel à mutationService
      // selon le columnId :
      //   'a-programmer' → mutationService.updatePropertyPlanification(visit.id, form.planifiedAt)
      //   'a-attribuer'  → mutationService.updatePropertyTechnicalManager(visit.id, form.managerId)
      //   'en-cours'     → mutationService.executeMutation({ entity: 'visit', type: 'INSERT', … })
      this.$emit('transition-confirmed', {
        visitId: this.visit.id,
        columnId: this.visit.columnId,
        formData: { ...this.form }
      })
    }
  }
}
</script>

<style lang="scss" scoped>
@use './KanbanTransitionModal.scss';
</style>
